import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2, SecurityContext } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AppInitService } from 'src/app/shared/modules/core/services/app-initializer.service';
import { ConfigService, CurrentFacilityService, InitialConfigurationService } from 'src/app/shared/services';
import { BannersService } from 'src/app/shared/services/banners.service';
import { FacilityConfigurationService } from 'src/app/shared/services/facility-configuration.service';
import { portalsMaintenance } from 'src/app/shared/utils/notifications';
import { DHLFacilityConfig } from './config/facility-config';
import { DHLFacilityType } from './models';
import { DHLConfigurationService } from './services/dhl-config.service';

@Component({
    selector: 'app-root',
    templateUrl: './dhl.component.html',
    styleUrls: ['./dhl.component.scss'],
    standalone: false,
})
export class DHLComponent implements OnInit, OnDestroy {
    private readonly appFavicon = this.document.getElementById('appFavicon');
    private unsubscribe$: Subject<void> = new Subject<void>();
    public isLoading$: Observable<boolean>;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private titleService: Title,
        private currentFacilityService: CurrentFacilityService,
        private sanitizer: DomSanitizer,
        private initialConfigurationService: InitialConfigurationService,
        private configService: ConfigService,
        private dhlConfigurationService: DHLConfigurationService,
        private appInitService: AppInitService,
        private facilityConfigurationService: FacilityConfigurationService,
        private readonly dhlFacilityConfig: DHLFacilityConfig,
        private readonly bannersService: BannersService
    ) {
        this.isLoading$ = this.appInitService.isLoading$;
    }

    ngOnInit(): void {
        this.currentFacilityService.setFacilitySource(DHLFacilityType.DHL);
        this.currentFacilityService.setInitialConfiguration({
            indexTitle: 'DHL Air Cargo',
            urlFavIcon: 'assets/dhl-aviation/img/favicon.ico',
            mainLogoLightUrl: 'dhl-aviation/img/dhl-logo-light.webp',
            mainLogoDarkUrl: 'dhl-aviation/img/dhl-logo-dark.webp',
            urlAdministrative: '',
            name: 'DHL Air Cargo',
            email: 'support@cargosprint.com',
            termsOfUseURL: 'https://cargosprint.com/legal/terms-of-use/#sprintpay',
            privacyAndPolicyURL: 'https://cargosprint.com/legal/privacy-policy',
            alternativeLogoDarkUrl: 'dhl-aviation/img/logo-black.webp',
            alternativeLogoLightUrl: 'dhl-aviation/img/logo.webp',
            setAlternativeLogoToHeader: true,
        });
        this.setFacilityConfiguration();
        this.subscribeInitialConfiguration();
        this.setGlobalConfig();
        this.setFavicon();
        this.setTitle();
        this.displayMaintenanceBanner();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private displayMaintenanceBanner(): void {
        this.bannersService.setBanners([portalsMaintenance]);
    }

    setGlobalConfig(): void {
        const config = this.dhlConfigurationService.getGlobalConfigurationBySource();

        this.configService.setChargesURL(config.setChargesURL);
        this.configService.enableAccesories(config.enableAccesories);
    }

    setFavicon(): void {
        const favIcon = this.sanitizer.sanitize(SecurityContext.URL, this.currentFacilityService.getUrlFavIcon()) || '';
        this.renderer.setAttribute(this.appFavicon, 'href', favIcon);
    }

    setTitle(): void {
        const title = this.currentFacilityService.getIndexTitle();
        this.titleService.setTitle(title);
    }

    subscribeInitialConfiguration(): void {
        this.initialConfigurationService.setInitialConfiguration().pipe(takeUntil(this.unsubscribe$)).subscribe();
    }

    setFacilityConfiguration(): void {
        this.facilityConfigurationService.setFacilityConfiguration(this.dhlFacilityConfig.getConfigDynamicField());
    }
}
